import { capitalize, SuretyQuoteStatus } from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import { useAdminFetchCarrierAgencyRelationships } from "@portal/api/bond-request/admin";
import {
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
	FormSection,
} from "@portal/components/form";
import { FormDropdownComponent } from "@portal/components/form/fieldLayouts";
import { FormDropdown, FormPercent } from "@portal/components/form/fields";
import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getMissingRequiredForApprovalLabels } from "../../../../../../utils/schema-fields";
import type { AdminCombinedFormData } from "../../schema";

type BondTermsFormData = Pick<
	AdminCombinedFormData,
	"accountTerms" | "bondTerms" | "quote" | "agencyId"
>;

const statusOptions = [
	{ label: capitalize(SuretyQuoteStatus.approved), value: SuretyQuoteStatus.approved },
	{ label: capitalize(SuretyQuoteStatus.declined), value: SuretyQuoteStatus.declined },
];

export const BondTermsStep = () => {
	const formMethods = useFormContext<BondTermsFormData>();
	const { control, watch, getValues, formState } = formMethods;
	const schema = useFormSchema();

	const accountTermsStatus = useWatch({ control, name: "accountTerms.status" });
	const bondTerms = watch("bondTerms");
	const quoteValues = watch("quote");

	const isAccountApproved = accountTermsStatus === "approved";

	const hasFormErrors = Object.keys(formState.errors).some((key) => key !== "bondTerms");

	const approvalBlockerMessage = useMemo(() => {
		if (!isAccountApproved) {
			return "Account must be approved to set bond terms";
		}

		const quoteSchema = schema?.shape?.quote;
		const missingLabels = quoteSchema
			? getMissingRequiredForApprovalLabels(quoteSchema, getValues().quote)
			: [];

		if (missingLabels.length > 0) {
			return `Cannot approve with missing fields: ${missingLabels.join(", ")}`;
		}

		return null;
	}, [quoteValues, isAccountApproved, schema]);

	const statusDisabled = hasFormErrors;
	const canApprove = approvalBlockerMessage === null;
	const disabledOptions = isAccountApproved
		? canApprove
			? []
			: ["approved"]
		: ["approved", "declined"];

	return (
		<FormSection
			topPadding
			header="Bond Terms"
			contentClassName="h-full min-h-[300px] flex flex-col space-y-6"
		>
			<FormFieldGroup>
				<FormDropdownComponent
					label="Status"
					name="bondTerms.status"
					subLabel={
						statusDisabled ? (
							<div className="text-yellow-700">
								Please resolve all form errors to select a status
							</div>
						) : approvalBlockerMessage ? (
							<div className="text-yellow-700">{approvalBlockerMessage}</div>
						) : undefined
					}
					options={statusOptions}
					formMethods={formMethods}
					disabled={statusDisabled}
					disabledOptions={disabledOptions}
					deselectable
				/>
			</FormFieldGroup>
			{bondTerms?.status === SuretyQuoteStatus.approved && <AcceptedTermsFields />}
		</FormSection>
	);
};

const AcceptedTermsFields = () => {
	const { control, watch, setValue } = useFormContext<BondTermsFormData>();
	const agencyId = watch("agencyId");
	const carrierName = watch("bondTerms.carrierName");
	const agencyCommissionPercentage = watch("bondTerms.agencyCommissionPercentage");
	const { allCarriers } = useAdminGetCarrierPreviews();
	const { carrierAgencyRelationships, carrierAgencyRelationshipsLoading } =
		useAdminFetchCarrierAgencyRelationships(agencyId);

	useEffect(() => {
		if (carrierAgencyRelationships) {
			for (const relationship of carrierAgencyRelationships) {
				if (
					carrierName === relationship.carrierName &&
					relationship.agencyCommissionPercentage &&
					agencyCommissionPercentage !== relationship.agencyCommissionPercentage
				) {
					setValue("bondTerms.agencyCommissionPercentage", relationship.agencyCommissionPercentage);
				}
			}
		}
	}, [carrierName, agencyId, carrierAgencyRelationshipsLoading]);

	if (!allCarriers || !allCarriers.length) return null;

	return (
		<div className="flex flex-col space-y-6 w-full">
			<FormFieldGroup>
				<FormItemLabel required>Carrier</FormItemLabel>
				<FormDropdown
					control={control}
					name="bondTerms.carrierName"
					options={allCarriers.map((carrier) => ({
						label: carrier.displayName,
						value: carrier.name,
					}))}
					placeholder="Select Carrier"
				/>
			</FormFieldGroup>
			<FormRow>
				<FormField>
					<FormItemLabel required>Rate (%)</FormItemLabel>
					<FormPercent control={control} name="bondTerms.rate" decimalPlaces={3} />
				</FormField>
				<FormField>
					<FormItemLabel required>Agency Commission (%)</FormItemLabel>
					<FormPercent control={control} name="bondTerms.agencyCommissionPercentage" />
				</FormField>
			</FormRow>
		</div>
	);
};
